<template>
  <div>
    <v-container class="px-0">
      <v-row no-gutters>
        <v-col cols="5" class="px-2">
          <v-card class="mx-auto" outlined>
            <v-list-item three-line>
              <v-list-item-content>
                <div class="overline mb-4">{{ currentStudy.name }}</div>
                <v-list-item-title class="headline mb-1">{{
                  currentStudy.pID
                }}</v-list-item-title>
              </v-list-item-content>

              <v-list-item-action
                v-if="userRole === 'admin' || userRole === 'manager'"
              >
                <v-menu bottom left>
                  <template v-slot:activator="{ on }">
                    <v-btn color="grey" icon v-on="on">
                      <v-icon>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>

                  <v-list class="text-left">
                    <v-list-item @click="openParticipantDetails">
                      <v-list-item-content>
                        <v-list-item-title
                          >Edit participant details</v-list-item-title
                        >
                      </v-list-item-content>
                      <v-list-item-icon>
                        <v-icon>mdi-pencil</v-icon>
                      </v-list-item-icon>
                    </v-list-item>
                    <v-list-item
                      @click="askToSendMoodFeedback"
                      :disabled="currentStudy.requiredToSendMoodFeedback"
                    >
                      <v-list-item-content>
                        <v-list-item-title>Ask to send mood</v-list-item-title>
                      </v-list-item-content>
                      <v-list-item-icon>
                        <v-icon>mdi-emoticon-outline</v-icon>
                      </v-list-item-icon>
                    </v-list-item>
                    <v-list-item
                      @click="askToOpenSurvey"
                      :disabled="currentStudy.requiredToOpenSurvey"
                    >
                      <v-list-item-content>
                        <v-list-item-title
                          >Ask to fill survey</v-list-item-title
                        >
                      </v-list-item-content>
                      <v-list-item-icon>
                        <v-icon>mdi-forum</v-icon>
                      </v-list-item-icon>
                    </v-list-item>
                    <v-divider></v-divider>
                    <v-list-item @click="confirmResetStudy">
                      <v-list-item-content>
                        <v-list-item-title>Reset study</v-list-item-title>
                      </v-list-item-content>
                      <v-list-item-icon>
                        <v-icon>mdi-file-restore</v-icon>
                      </v-list-item-icon>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </v-list-item-action>
            </v-list-item>

            <v-container class="grey lighten-5 body-2" v-if="patientDetails">
              <v-row
                no-gutters
                v-for="detail in patientDetails"
                :key="detail.key"
                class="d-flex align-center py-1"
                style="border-bottom: 1px solid rgba(0,0,0,0.1)"
              >
                <v-col cols="6">
                  {{ detail.title }}
                </v-col>
                <v-col cols="6" class="font-weight-bold">
                  <template v-if="detail.key === 'hba1c'">
                    <div
                      v-for="(entry, index) in detail.value"
                      :key="'detail-hba1c-' + index"
                    >
                      {{ entry.value }}% ({{
                        entry.date | moment("DD-MMM-YYYY")
                      }})
                    </div>
                  </template>
                  <template v-else>
                    {{ detail.value }}
                  </template>
                </v-col>
              </v-row>
            </v-container>
          </v-card>
        </v-col>
        <v-col cols="7" class="px-2">
          <v-card class="mx-auto" outlined>
            <v-card-text>
              <p class="headline text--primary">
                Internal Notes
              </p>

              <div id="notesContent">
                <div v-if="currentAdminNotes.length > 0">
                  <div
                    class="notes-row"
                    v-for="note in currentAdminNotes"
                    :key="note.id"
                  >
                    <div class="notes-row-header">
                      <div>
                        <md-avatar
                          class="md-avatar-icon md-small md-primary"
                          v-if="note.flagged"
                        >
                          <md-icon>flag</md-icon>
                          <md-tooltip md-direction="right"
                            >Algorithm parameters flagged</md-tooltip
                          >
                        </md-avatar>
                        <md-avatar
                          class="md-avatar-icon md-small md-primary"
                          v-else-if="note.params"
                        >
                          <md-icon>settings</md-icon>
                          <md-tooltip md-direction="right"
                            >Params changed on
                            {{
                              note.timestamp | moment("MMM D, HH:mm")
                            }}</md-tooltip
                          >
                        </md-avatar>
                        <md-chip>{{
                          dbAdminUsers[note.adminUserID].name
                        }}</md-chip>
                      </div>
                      <div>{{ note.timestamp | moment("from") }}</div>
                    </div>
                    <div class="notes-row-content">{{ note.note }}</div>
                  </div>
                </div>
                <div v-else>
                  There aren't any notes yet...
                </div>
              </div>
            </v-card-text>

            <v-card-actions v-if="userRole !== 'viewer'">
              <v-form
                ref="formNote"
                v-model="validNote"
                lazy-validation
                class="d-flex px-2 flex-grow-1"
              >
                <v-textarea
                  label="Message"
                  v-model="noteInput"
                  auto-grow
                  solo
                  rows="1"
                  row-height="5"
                  :rules="[$rules.required]"
                  append-outer-icon="mdi-send"
                  @click:append-outer="sendNote"
                ></v-textarea>
              </v-form>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <md-dialog :md-active.sync="modalVisible">
      <md-dialog-title>Reset {{ currentStudy.pID }}'s study?</md-dialog-title>
      <md-dialog-content>
        Resetting a study will reset all the parameters and logs entered for
        that user. It will instantly bring the user back to the start study
        screen (unless offline). This operation could take up to a minute or
        two.
        <br /><br />
        <b>Warning:</b> This action is irreversible.
      </md-dialog-content>
      <md-dialog-actions>
        <md-button @click="onCancelReset">Cancel</md-button>
        <md-button class="md-raised md-accent" @click="onConfirmReset"
          >Reset</md-button
        >
      </md-dialog-actions>
    </md-dialog>

    <v-dialog v-model="editDetails" scrollable max-width="400px">
      <v-card v-if="editParticipantDetails">
        <v-card-title>Participant details</v-card-title>
        <v-divider></v-divider>
        <v-card-text style="height: 300px;" class="pt-4">
          <template v-for="(value, key) in participantDetailsTitles">
            <div v-if="key === 'hba1c'" :key="key" class="mt-4">
              <v-divider />
              <div class="title text--primary my-4">HbA1c</div>
              <div
                class="d-flex align-center mb-2"
                v-for="(entry, index) in editParticipantDetails[key]"
                :key="'hba1c-entry-' + index"
              >
                <v-text-field
                  label="HbA1c (%)"
                  type="number"
                  outlined
                  hide-details="auto"
                  v-model="entry.value"
                  dense
                ></v-text-field>
                <v-text-field
                  label="Date of measurement"
                  type="date"
                  outlined
                  class="mx-2"
                  hide-details="auto"
                  v-model="entry.date"
                  dense
                ></v-text-field>
                <v-btn icon outlined @click="removeHba1cEntry(index)"
                  ><v-icon>mdi-close</v-icon></v-btn
                >
              </div>
              <v-btn @click="addHba1cEntry" class="mt-2">Add an entry</v-btn>
            </div>
            <template v-else-if="value.type == 'date-picker'">
              <v-menu
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
                :key="key"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="editParticipantDetails[key]"
                    prepend-inner-icon="event"
                    readonly
                    outlined
                    :label="value.title"
                    v-on="on"
                    class="mb-2"
                    hide-details
                    :disabled="
                      key === 'personalSensorModelStartDate' &&
                        (!editParticipantDetails['personalSensorModel'] ||
                          editParticipantDetails['personalSensorModel'] ===
                            'n/a')
                    "
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="editParticipantDetails[key]"
                  no-title
                  :allowed-dates="
                    key === 'studyStartDate' ? allowedStudyStartDates : null
                  "
                ></v-date-picker>
              </v-menu>
            </template>
            <template v-else-if="value.type == 'select'">
              <v-select
                :items="
                  key === 'personalSensorModel' ? sensorModelsItems : sexItems
                "
                v-model="editParticipantDetails[key]"
                :label="value.title"
                hide-details="auto"
                class="mb-2"
                outlined
                :key="key"
              ></v-select>
            </template>
            <v-text-field
              :label="value.title"
              :type="value.type"
              outlined
              hide-details="auto"
              class="mb-2"
              v-model="editParticipantDetails[key]"
              :key="key"
              v-else
            ></v-text-field>
          </template>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer />
          <v-btn text color="red" @click="editDetails = false">Cancel</v-btn>
          <v-btn color="primary" @click="saveParticipantDetails">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.notes-card {
  flex: 1;
}
.notes-card .md-card-content {
  max-height: 200px;
  overflow-y: scroll;
}
.notes-card .md-card-actions > div {
  margin: 5px 10px;
}

#notesContent {
  max-height: 200px;
  overflow-y: scroll;
}

.notes-row {
  margin-top: 10px;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  padding-top: 10px;
}
.notes-row .notes-row-header {
  font-size: 0.8em;
  display: flex;
  justify-content: space-between;
  margin-bottom: 5px;
}
.notes-row .notes-row-header .md-avatar {
  margin-right: 10px;
}
.notes-row .notes-row-header .md-chip {
  font-size: 1em;
}
.notes-row .notes-row-content {
  margin-left: 10px;
}
</style>

<script>
import {
  PARTICIPANT_DETAILS_TITLES,
  SEX_ITEMS,
  SENSOR_MODELS_ITEMS
} from "../globals";
import { mapState } from "vuex";
import dayjs from "dayjs";
import moment from "moment";

export default {
  name: "user-first-row",
  props: ["groupDetails"],
  data: () => ({
    modalVisible: false,
    showDetails: false,

    noteInput: null,
    validNote: true,
    patientDetails: null,
    editDetails: false,
    participantDetailsTitles: PARTICIPANT_DETAILS_TITLES,
    sexItems: SEX_ITEMS,
    sensorModelsItems: SENSOR_MODELS_ITEMS,
    editParticipantDetails: null
  }),
  computed: {
    ...mapState([
      "dbGroups",
      "dbAdminUsers",
      "currentAdminNotes",
      "currentStudy",
      "userRole",
      "currentParticipantDetails",
      "weekStartDay"
    ]),
    params: function() {
      return this.currentStudy.params;
    },
    participantAge: function() {
      return this.currentParticipantDetails.dateOfBirth
        ? dayjs().diff(this.currentParticipantDetails.dateOfBirth, "years")
        : "N/A";
    },
    participantDiagnosed: function() {
      return this.currentParticipantDetails.dateOfDiagnosis
        ? dayjs().diff(this.currentParticipantDetails.dateOfDiagnosis, "years")
        : "N/A";
    }
  },
  updated() {
    if (this.currentAdminNotes.length) {
      let notesContainer = document.getElementById("notesContent");
      notesContainer.scrollTop = notesContainer.scrollHeight;
    }
  },
  watch: {
    currentParticipantDetails: function(val) {
      this.patientDetails = [
        {
          key: "group",
          title: "Group",
          value: `${this.groupDetails.groupName || "[no group assigned]"} (${
            this.groupDetails.name
          })`
        },
        {
          key: "meal-strategy",
          title: "Meal strategy",
          value: this.params.isAM ? "Carbohydrate counting" : "Fix dose"
        },
        {
          key: "hba1c",
          title: "HbA1c",
          value: val ? val.hba1c : ""
        },
        {
          key: "age",
          title: "Age (years diagnosed), sex",
          value: val
            ? `${this.participantAge} (${this.participantDiagnosed}), ${val.sex}`
            : ""
        },
        {
          key: "height-weight",
          title: "BMI (height, weight)",
          value: this.getBmiHeightWeight()
        },
        {
          key: "target-glucose",
          title: "Target glucose (ISF)",
          value: `${this.params.targGlucose} mmol/L (${this.params.insuSensFactor} mmol/L/U)`
        },
        {
          key: "insulin-duration",
          title: "Insulin duration (basal/bolus increment)",
          value: `${this.params.activeInsulinTime}h (${this.params.incrBasal}U, ${this.params.incrBolus}U)`
        },
        {
          key: "personal-sensor",
          title: "Previous sensor use (duration)",
          value:
            val &&
            `${this.getSensorModel(val.personalSensorModel)} ${
              val.admissionVisitDate && val.personalSensorModelStartDate
                ? "(" +
                  Math.abs(
                    moment(val.admissionVisitDate).diff(
                      moment(val.personalSensorModelStartDate),
                      "months"
                    )
                  ) +
                  " month(s))"
                : ""
            }`
        },
        {
          key: "study-start-date",
          title: "Study start date",
          value:
            val && val.studyStartDate
              ? moment(val.studyStartDate).format("DD-MMM-YYYY")
              : ""
        },
        {
          key: "admission-visit-date",
          title: "Admission visit date",
          value:
            val && val.admissionVisitDate
              ? moment(val.admissionVisitDate).format("DD-MMM-YYYY")
              : ""
        },
        {
          key: "study-end-date",
          title: "Study end date",
          value:
            val && val.studyEndDate
              ? moment(val.studyEndDate).format("DD-MMM-YYYY")
              : ""
        },
        {
          key: "study-end-visit-date",
          title: "End-of-study visit date",
          value:
            val && val.studyEndVisitDate
              ? moment(val.studyEndVisitDate).format("DD-MMM-YYYY")
              : ""
        }
      ];
    }
  },
  methods: {
    confirmResetStudy() {
      this.modalVisible = true;
    },
    onCancelReset() {
      this.modalVisible = false;
    },
    onConfirmReset() {
      this.$store.dispatch("addResetStudyRequest", this.currentStudy);
      this.modalVisible = false;

      this.$router.push({ path: "/home" });
    },
    askToSendMoodFeedback() {
      this.$store.dispatch("askToSendMoodFeedback", this.currentStudy.id);
    },
    askToOpenSurvey() {
      this.$store.dispatch("askToOpenSurvey", this.currentStudy.id);
    },
    getBmiHeightWeight() {
      if (this.currentParticipantDetails) {
        const { height, weight } = this.currentParticipantDetails;
        let bmi = "";
        if (height && weight) {
          bmi = (Number(weight) / Math.pow(Number(height) / 100, 2)).toFixed(1);
        }

        return `${bmi} (${height ? height + "cm" : ""}, ${
          weight ? weight + "kg" : ""
        })`;
      } else {
        return "";
      }
    },
    getSensorModel(sensorKey) {
      let returnText = "";
      if (sensorKey) {
        let sensorModelObj = this.sensorModelsItems.find(
          model => model.value === sensorKey
        );

        if (sensorModelObj) {
          returnText = sensorModelObj.text;
        } else {
          returnText = sensorKey;
        }
      }
      return returnText;
    },
    sendNote() {
      if (this.$refs.formNote.validate()) {
        let obj = {
          note: this.noteInput,
          study: this.currentStudy.id
        };
        this.$store.dispatch("pushNote", obj);

        this.$refs.formNote.reset();
      }
    },
    getGroup(groupId) {
      return this.dbGroups.find(group => group.id === groupId);
    },
    assignedEndo(groupId) {
      let group = this.getGroup(groupId);
      if (!group || !group.assignedTo) {
        return null;
      }

      return this.dbAdminUsers[group.assignedTo];
    },
    openParticipantDetails() {
      let objCopy = this.currentParticipantDetails
        ? JSON.parse(JSON.stringify(this.currentParticipantDetails))
        : {};
      if (!("hba1c" in objCopy)) {
        objCopy["hba1c"] = [];
      }
      this.editParticipantDetails = objCopy;
      this.editDetails = true;
    },
    addHba1cEntry() {
      this.editParticipantDetails.hba1c.push({ value: "", date: "" });
    },
    removeHba1cEntry(index) {
      this.editParticipantDetails.hba1c.splice(index, 1);
    },
    saveParticipantDetails() {
      this.editDetails = false;

      if ("hba1c" in this.editParticipantDetails) {
        this.editParticipantDetails.hba1c.sort(
          (a, b) => new Date(a.date) - new Date(b.date)
        );
      }

      this.$store.dispatch(
        "saveParticipantDetails",
        this.editParticipantDetails
      );
    },
    allowedStudyStartDates(val) {
      if (this.weekStartDay) {
        return moment(val).isoWeekday() === this.weekStartDay;
      } else {
        return false;
      }
    }
  }
};
</script>
